import IFeatureController from "../IFeatureController";
import { GridMaterial } from "babylonjs-materials";
import { Matrix } from "babylonjs";
import { TrackingStatus } from "@webcore/apps/app.8w";
import {
  Color3,
  MeshBuilder,
  Vector3,
  Mesh,
  TransformNode,
  PointerEventTypes
} from "babylonjs";
import { Main } from "@/main.app";
import appEvents from "@/events/events";
import { AppMode } from "@/context/AppContext";
import { getCurrentMode } from "@/context/AppContextStore";

class GroundPlacementController implements IFeatureController {
  private app: Main;
  private surface?: Mesh;
  private contentNode?: TransformNode;
  private readonly directionVector: Vector3 = new Vector3();
  private readonly identityMatrix: Matrix = Matrix.Identity();
  private interactionEnabled: boolean = false;
  private canPlaceContent: boolean = false;
  private renderObserver?: any;
  private pointerObserver?: any;
  private isInitialized: boolean = false;

  constructor(app: Main) {
    this.app = app;
    this.initModeListener();
  }

  public init(){
    // Persist tracking status listener between modes (don't dispose)
    this.initTrackingStatusListener();
  }

  public initialize(){
    if(this.isInitialized || getCurrentMode() !== AppMode.GROUND) return;
    this.initContent();
    this.initInteractionListeners();
    this.isInitialized = true;
  }

  private initModeListener(): void {
    appEvents.on('app-mode-changed', (this.updateMode.bind(this)));
  }

  private initTrackingStatusListener(): void {
    appEvents.on('tracking-status-changed', this.updateTrackingStatus.bind(this));
  }

  private initContent(): void {
    const anchorManager = this.app.getAnchorManager();
    if (!anchorManager) {
      console.error("AnchorManager not available");
      return;
    }
    
    this.contentNode = anchorManager.createGroundAnchor();
    this.placeContentInFrontOfCamera(3);
    this.initGrid();
  }

  private initGrid(): void {
    const ground = MeshBuilder.CreatePlane("ground", { size: 2000 }, this.app.getScene()!);
    ground.rotation.x = Math.PI / 2;
    
    const gridMaterial = new GridMaterial("grid", this.app.getScene()!);
    gridMaterial.mainColor = new Color3(0.2, 0.2, 0.3);
    gridMaterial.lineColor = new Color3(0, 0.7, 1);
    gridMaterial.opacity = import.meta.env.DEV ? 0.3 : 0;
    
    ground.material = gridMaterial;
    this.surface = ground;
  }

  private placeContentInFrontOfCamera(distance: number): void {
    if (!this.app.getScene()?.activeCamera || !this.contentNode) return;
    const camera = this.app.getScene().activeCamera;
    if(!camera) return;
    const position = camera.position.add(camera.getDirection(Vector3.Forward()).scale(distance));
    position.y = 0;
    this.contentNode.position.copyFrom(position);
    this.updateContentOrientation();
  }

  private initInteractionListeners(): void {
    this.pointerObserver = this.app.getScene()!.onPointerObservable.add((pointerInfo) => {;
      if (!this.interactionEnabled || pointerInfo.type !== PointerEventTypes.POINTERDOWN) return;
      
      const event = pointerInfo.event;
      const pickRay = this.app.getScene()!.createPickingRay(event.clientX, event.clientY, this.identityMatrix, this.app.getScene()!.activeCamera!);
      const pickResult = this.app.getScene()!.pickWithRay(pickRay);
      if (pickResult?.hit && pickResult.pickedMesh === this.surface) {
        this.onGroundTap(pickResult.pickedPoint!);
      }
    });
  }

  private onGroundTap(position: Vector3): void {
    if (this.contentNode) {
      this.contentNode.position.copyFrom(position);
      this.updateContentOrientation();
    }
  }

  private updateContentOrientation(): void {
    if (!this.contentNode || !this.app.getScene()?.activeCamera) return;
    
    const camera = this.app.getScene().activeCamera;
    if(!camera) return;
    camera.position.subtractToRef(this.contentNode.position, this.directionVector);
    this.directionVector.normalize();
    this.directionVector.y = 0;
    
    if (this.directionVector.length() < 0.1) this.directionVector.z = 1;
    this.contentNode.rotation.y = Math.atan2(this.directionVector.x, this.directionVector.z);
  }

  private updateMode(mode: AppMode): void {
    if (mode === AppMode.GROUND) {
      this.initialize();
    } else {
      this.dispose();
    }
  }

  private updateTrackingStatus(status: TrackingStatus): void {
    const wasInteractionEnabled = this.interactionEnabled;
    if (status === "NORMAL") {
      this.canPlaceContent = true;
      this.interactionEnabled = true;
    } else if (status === "LIMITED" && this.canPlaceContent) {
      this.interactionEnabled = true;
    } else {
      this.interactionEnabled = false;
    }

    if(this.isInitialized || getCurrentMode() !== AppMode.GROUND) return;

    if (
      !wasInteractionEnabled &&
      this.interactionEnabled &&
      this.canPlaceContent
    ) {
      this.placeContentInFrontOfCamera(3);
    }
  }

  public dispose(): void {
    if(!this.isInitialized) return;
    this.isInitialized = false;
    
    // Remove observers
    if (this.renderObserver) {
      this.app.getScene()?.unregisterBeforeRender(this.renderObserver);
      this.renderObserver = null;
    }
    
    if (this.pointerObserver) {
      this.app.getScene()?.onPointerObservable.remove(this.pointerObserver);
      this.pointerObserver = null;
    }
    
    // Dispose of controller's own resources (grid surface)
    if (this.surface) {
      this.surface.dispose();
      this.surface = undefined;
    }
    
    // Release reference to anchor (AnchorManager handles cleanup via mode change)
    this.contentNode = undefined;
    
    // Reset state
    //this.canPlaceContent = false;
  }
}

export default GroundPlacementController;
